/**
 * Recipient-list parsing for the campaign composer (PE-2125).
 *
 * The BFF validates `recipientEmails` with `@ArrayMinSize(1)` and
 * `@IsEmail({}, { each: true })`, and a rejection there comes back as a 502 with
 * class-validator's message — useless to a sales admin pasting 200 addresses,
 * because it never says WHICH address was bad. So the parsing and the per-address
 * verdict happen here, client-side, before anything is sent.
 *
 * Pure functions, no React — this is the part worth unit-testing.
 */

/**
 * Deliberately stricter than "has an @": it must have a non-empty local part, a
 * dotted domain, and no whitespace. class-validator's `isEmail` would reject the
 * same inputs, and matching it here is the whole point — a mismatch means the
 * user gets a 502 instead of an inline error.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

export function isValidRecipient(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

export interface ParsedRecipients {
  /** Unique, trimmed, valid addresses — the array to POST. */
  valid: string[];
  /** Entries that aren't addresses, in input order, deduped. */
  invalid: string[];
  /** How many duplicates were collapsed out of `valid`. */
  duplicateCount: number;
}

/**
 * Splits a free-text blob into recipients. Accepts commas, semicolons, and any
 * whitespace (including newlines) as separators, because the realistic input is
 * a paste out of a spreadsheet column or an email client's To: field.
 *
 * Deduplication is case-insensitive on the whole address. That's not strictly
 * correct — the local part is case-sensitive per RFC 5321 — but no real provider
 * treats it that way, and sending one person the same offer twice because they
 * appear as both `Ada@` and `ada@` is the worse failure.
 */
export function parseRecipients(raw: string): ParsedRecipients {
  const tokens = raw
    .split(/[,;\s]+/)
    .map((t) => t.trim())
    .filter(Boolean);

  const valid: string[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();
  let duplicateCount = 0;

  for (const token of tokens) {
    if (!isValidRecipient(token)) {
      if (!invalid.includes(token)) invalid.push(token);
      continue;
    }
    const key = token.toLowerCase();
    if (seen.has(key)) {
      duplicateCount += 1;
      continue;
    }
    seen.add(key);
    valid.push(token);
  }

  return { valid, invalid, duplicateCount };
}

/**
 * Whether the composer may submit. Mirrors the server's own preconditions so the
 * button disables instead of the request 502-ing: every field the DTO marks
 * `@IsNotEmpty` must be present, at least one recipient must parse, and no
 * unparseable leftovers may remain (sending a partially-bad list silently drops
 * addresses the user believes they entered).
 */
export function canSubmitCampaign(input: {
  title: string;
  tenantId: string;
  templateId: string;
  recipients: ParsedRecipients;
}): boolean {
  return (
    input.title.trim().length > 0 &&
    input.tenantId.length > 0 &&
    input.templateId.trim().length > 0 &&
    input.recipients.valid.length > 0 &&
    input.recipients.invalid.length === 0
  );
}

/** Delivery-status tone for the logs table. Unknown statuses stay neutral. */
export function logStatusTone(status: string): 'success' | 'error' | 'neutral' {
  const s = status.toLowerCase();
  if (s.includes('fail') || s.includes('error') || s.includes('bounce') || s.includes('reject')) {
    return 'error';
  }
  if (s.includes('sent') || s.includes('deliver') || s.includes('success') || s.includes('queued')) {
    return 'success';
  }
  return 'neutral';
}

/** Counts for the logs summary strip, derived from whatever statuses came back. */
export function summariseLogs(logs: { status: string }[]): {
  total: number;
  delivered: number;
  failed: number;
  other: number;
} {
  let delivered = 0;
  let failed = 0;
  let other = 0;
  for (const log of logs) {
    const tone = logStatusTone(log.status);
    if (tone === 'success') delivered += 1;
    else if (tone === 'error') failed += 1;
    else other += 1;
  }
  return { total: logs.length, delivered, failed, other };
}
